import React from 'react';
import { Avatar, Box } from '@mui/material';
import { ringVisual, borderWidthRatio } from '../../constants/avatarBorder';

/**
 * 縁取り付きアバター。
 *
 * 回転はグラデーションの角度ではなく要素の transform で行う。
 * @property で角度を回すとフレームごとに再ペイントが走るが、
 * 要素を回せばコンポジタだけで完結する。
 *
 * リングは外側に足さず内側を削る（size は縁込みの総寸法）ので、
 * 差し替えてもレイアウトが動かない。
 */

type Size = number | { xs: number; md: number };

const scale = (s: Size, f: (n: number) => number) =>
  typeof s === 'number' ? f(s) : { xs: f(s.xs), md: f(s.md) };

// 回す層は必ずレイヤーへ昇格させる。昇格していないと conic-gradient を
// 毎フレームラスタライズすることになる（実測で1個あたり30%近く出た）。
const spinSx = (spin: number | undefined, name: string) =>
  spin
    ? {
        [`@keyframes ${name}`]: {
          to: { transform: `rotate(${spin < 0 ? -360 : 360}deg)` },
        },
        animation: `${name} ${Math.abs(spin)}s linear infinite`,
        willChange: 'transform',
        '@media (prefers-reduced-motion: reduce)': { animation: 'none', willChange: 'auto' },
      }
    : {};

const AvatarRing: React.FC<{
  src?: string;
  alt?: string;
  size: Size;
  borderKey: string;
  /** リングの色に使うアクセント色（hex） */
  accent: string;
  widthKey?: string;
  /**
   * false で回転を止める。**一覧・プレビューなど複数個並べる場所では必ず false。**
   * mix-blend-mode を持つ層は合成だけで完結できず毎フレーム再ペイントになるため、
   * 数が並ぶと conic-gradient のラスタライズが積み上がる（設定パネルだけで実測100%だった）。
   */
  animated?: boolean;
}> = ({ src, alt, size, borderKey, accent, widthKey, animated = true }) => {
  const v = ringVisual(borderKey, accent);
  // 太さは寸法に対する比。プレビュー(40px)でも段階差が潰れないようにするため。
  const ratio = borderWidthRatio(widthKey);
  return (
    <Box
      sx={{
        position: 'relative',
        flexShrink: 0,
        width: scale(size, n => n),
        height: scale(size, n => n),
        borderRadius: '50%',
        boxShadow: v.glow,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          background: v.base,
          ...(animated ? spinSx(v.spin, 'avatarRingSpin') : {}),
        }}
      />
      {v.overlay && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            background: v.overlay,
            // **mix-blend-mode を使わないこと。** ブレンドは背景を読む必要があるため
            // 合成だけで完結できず、回すと毎フレーム再ペイントになる。
            // 白の重ねなので、素の透過でもほぼ同じ見え方になる。
            // 濃さは ringVisual が決める（箔は母材の明度で変わる）
            opacity: v.overlayOpacity ?? 0.6,
            ...(animated ? spinSx(v.overlaySpin, 'avatarRingOverlay') : {}),
          }}
        />
      )}
      <Avatar
        src={src}
        alt={alt}
        sx={{
          position: 'absolute',
          top: scale(size, n => n * ratio),
          left: scale(size, n => n * ratio),
          width: scale(size, n => n - n * ratio * 2),
          height: scale(size, n => n - n * ratio * 2),
        }}
      />
    </Box>
  );
};

export default AvatarRing;
